import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Presentation, Headphones, Film, FileText, Play, Download, Pencil, Trash2, FolderPlus, GraduationCap, X, BarChart3, ListChecks, Award, Loader2 } from 'lucide-react';
import { QuizEditor, QuizPlayer } from '../components/formation/Quiz';
import { useMediaTracking, ProgressBar } from '../components/formation/tracking';
import { SuiviDialog } from '../components/formation/SuiviDialog';
import { AppLayout } from '../components/AppLayout';
import { PageHeader, SceneBand, SceneStat } from '../components/ds';
import { useAuth } from '../contexts/AuthContext';
import { FamilleDialog, ItemDialog } from '../components/formation/FormationDialogs';
import { SlideShow } from '../components/formation/SlideShow';
import { fileUrl, slideUrl, fmtSize } from '../components/formation/formationApi';

const ICONS = { book: Presentation, audio: Headphones, video: Film, document: FileText };
const TYPE_LABEL = { book: 'Diaporama', audio: 'Audio', video: 'Vidéo', document: 'Document' };
const iconBtn = 'w-8 h-8 rounded-ctrl flex items-center justify-center text-text-3 hover:text-ink hover:bg-surface-2 transition-colors';

function VideoModal({ item, token, onClose, onProgress }) {
  const { api } = useAuth();
  const track = useMediaTracking(item, api, onProgress);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" onClick={onClose} data-testid="video-modal">
      <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center" onClick={onClose} data-testid="video-close"><X className="w-5 h-5" /></button>
      <video src={fileUrl(item.id, 'main', token)} controls autoPlay {...track} className="max-w-full max-h-full rounded-lg" onClick={e => e.stopPropagation()} data-testid="video-player" />
    </div>
  );
}

function AudioPlayer({ item, token, onProgress }) {
  const { api } = useAuth();
  const track = useMediaTracking(item, api, onProgress);
  return <audio controls preload="metadata" src={fileUrl(item.id, 'main', token)} className="w-full mt-1" data-testid={`formation-audio-${item.id}`} {...track} />;
}

const stalled = (p) => !p?.heartbeat || Date.now() - new Date(p.heartbeat).getTime() > 300000;

function ItemCard({ item, token, isAdmin, onPlay, onEdit, onDelete, onProgress, onQuiz, onEditQuiz, onReprocess }) {
  const Icon = ICONS[item.type] || FileText;
  const hiddenN = new Set([...(item.hidden_global || []), ...(item.my_hidden || [])]).size;
  return (
    <div className="group rounded-card bg-surface border border-border/60 overflow-hidden flex flex-col transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(37,39,43,0.10)]" data-testid={`formation-item-${item.id}`}>
      {item.type === 'book' && item.slides_count > 0 ? (
        <button type="button" onClick={() => onPlay(item)} className="relative aspect-video bg-graphite-900 overflow-hidden" data-testid={`formation-cover-${item.id}`}>
          <img src={slideUrl(item, 1, token, true)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" loading="lazy" />
          <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
            <span className="w-12 h-12 rounded-full bg-orange text-orange-on flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Play className="w-5 h-5 ml-0.5" /></span>
          </span>
        </button>
      ) : (
        <div className="aspect-video bg-graphite-900 flex items-center justify-center"><Icon className="w-10 h-10 text-orange" strokeWidth={1.6} /></div>
      )}
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-label">
          <Icon className="w-3.5 h-3.5" /> {TYPE_LABEL[item.type]}
          {item.type === 'book' && <span className="normal-case tracking-normal font-medium text-text-3">· {item.slides_count} slides{hiddenN ? ` (${hiddenN} masquée(s))` : ''}</span>}
        </div>
        <h3 className="font-semibold text-ink leading-snug" data-testid={`formation-item-title-${item.id}`}>{item.titre}</h3>
        {item.description && <p className="text-[13px] text-text-3 whitespace-pre-line line-clamp-4">{item.description}</p>}
        {item.type === 'audio' && <AudioPlayer item={item} token={token} onProgress={onProgress} />}
        {item.type !== 'document' && item.my_progress > 0 && <ProgressBar value={item.my_progress} testid={`formation-my-progress-${item.id}`} />}
        {item.processing && !item.processing.error && (
          <div className="space-y-1" data-testid={`formation-processing-${item.id}`}>
            <p className="text-xs text-orange-700 font-medium flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Préparation des slides… {item.processing.done || 0} / {item.processing.total || '?'}</p>
            <ProgressBar value={item.processing.total ? (item.processing.done || 0) / item.processing.total * 100 : 0} />
          </div>
        )}
        {item.video_conv && !item.video_conv.error && !stalled(item.video_conv) && (
          <div className="space-y-1" data-testid={`formation-video-conv-${item.id}`}>
            <p className="text-xs text-orange-700 font-medium flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Optimisation de la vidéo… {item.video_conv.pct || 0} %</p>
            <ProgressBar value={item.video_conv.pct || 0} />
            <p className="text-[11px] text-text-3">La vidéo reste lisible pendant l'optimisation.</p>
          </div>
        )}
        {item.video_conv && (item.video_conv.error || stalled(item.video_conv)) && (
          <p className="text-xs text-red-600" data-testid={`formation-video-conv-error-${item.id}`}>{item.video_conv.error || "L'optimisation de la vidéo semble interrompue."}</p>
        )}
        {isAdmin && item.video_conv && (item.video_conv.error || stalled(item.video_conv)) && (
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => onReprocess(item)} data-testid={`formation-video-reconvert-${item.id}`}
              className="text-xs font-semibold text-orange-700 underline">Relancer l'optimisation</button>
            <button type="button" onClick={() => onReprocess(item, 'skip-conversion')} data-testid={`formation-video-skip-${item.id}`}
              className="text-xs font-semibold text-graphite-700 underline">Garder la vidéo d'origine</button>
          </div>
        )}
        {item.processing?.error && <p className="text-xs text-red-600" data-testid={`formation-processing-error-${item.id}`}>{item.processing.error}</p>}
        {isAdmin && item.processing && (item.processing.error || stalled(item.processing)) && (
          <button type="button" onClick={() => onReprocess(item)} data-testid={`formation-reprocess-${item.id}`}
            className="self-start text-xs font-semibold text-orange-700 underline">Relancer la préparation</button>
        )}
        <div className="mt-auto pt-2 flex items-center gap-1">
          {item.type === 'book' && item.slides_count > 0 && (
            <button onClick={() => onPlay(item)} className="h-9 px-3.5 rounded-ctrl bg-graphite-900 text-white text-sm font-semibold inline-flex items-center gap-2 hover:bg-graphite-800 transition-colors" data-testid={`formation-play-${item.id}`}>
              <Play className="w-4 h-4" /> Diffuser
            </button>
          )}
          {item.type === 'video' && (
            <button onClick={() => onPlay(item)} className="h-9 px-3.5 rounded-ctrl bg-graphite-900 text-white text-sm font-semibold inline-flex items-center gap-2 hover:bg-graphite-800 transition-colors" data-testid={`formation-play-${item.id}`}>
              <Play className="w-4 h-4" /> Regarder
            </button>
          )}
          {item.quiz_count > 0 && (
            <button onClick={() => onQuiz(item)} data-testid={`formation-quiz-${item.id}`}
              className={`h-9 px-3 rounded-ctrl border text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${item.my_quiz?.passed ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-orange/40 text-orange-700 hover:bg-orange/10'}`}
              title={item.my_quiz ? `Meilleur score ${item.my_quiz.best_score} % · ${item.my_quiz.attempts} tentative(s)` : `${item.quiz_count} question(s)`}>
              {item.my_quiz?.passed ? <Award className="w-4 h-4" /> : <ListChecks className="w-4 h-4" />}
              {item.my_quiz?.passed ? `Validé ${item.my_quiz.best_score} %` : item.my_quiz ? `Quiz ${item.my_quiz.best_score} %` : 'Quiz'}
            </button>
          )}
          <a href={fileUrl(item.id, item.files?.pptx ? 'pptx' : 'main', token, true)} className={iconBtn} title={`Télécharger ${item.files?.pptx?.name || item.files?.main?.name || ''} ${fmtSize((item.files?.pptx || item.files?.main)?.size)}`} data-testid={`formation-download-${item.id}`}>
            <Download className="w-4 h-4" />
          </a>
          {isAdmin && (
            <span className="ml-auto flex gap-1">
              <button className={iconBtn} onClick={() => onEditQuiz(item)} title={item.quiz_count ? 'Modifier le quiz' : 'Ajouter un quiz'} data-testid={`formation-edit-quiz-${item.id}`}><ListChecks className="w-4 h-4" /></button>
              <button className={iconBtn} onClick={() => onEdit(item)} title="Modifier" data-testid={`formation-edit-${item.id}`}><Pencil className="w-4 h-4" /></button>
              <button className={`${iconBtn} hover:text-red-600`} onClick={() => onDelete(item)} title="Supprimer" data-testid={`formation-delete-${item.id}`}><Trash2 className="w-4 h-4" /></button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function FamilleSection({ fam, token, isAdmin, onPlay, onEditItem, onDeleteItem, onAddItem, onEditFam, onDeleteFam, onProgress, onQuiz, onEditQuiz, onReprocess }) {
  return (
    <section className="space-y-4" data-testid={`formation-famille-${fam.id}`}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold uppercase text-2xl text-ink leading-tight">{fam.nom}</h2>
          {fam.description && <p className="text-sm text-text-3 mt-1 max-w-3xl whitespace-pre-line">{fam.description}</p>}
        </div>
        {isAdmin && (
          <div className="flex gap-1">
            <button className="h-9 px-3 rounded-ctrl border border-border text-sm text-ink inline-flex items-center gap-1.5 hover:bg-surface-2 transition-colors" onClick={() => onAddItem(fam.id)} data-testid={`famille-add-item-${fam.id}`}><Plus className="w-4 h-4" /> Élément</button>
            <button className={iconBtn} onClick={() => onEditFam(fam)} title="Modifier la famille" data-testid={`famille-edit-${fam.id}`}><Pencil className="w-4 h-4" /></button>
            <button className={`${iconBtn} hover:text-red-600`} onClick={() => onDeleteFam(fam)} title="Supprimer la famille" data-testid={`famille-delete-${fam.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      {fam.items.length ? (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {fam.items.map(it => <ItemCard key={it.id} item={it} token={token} isAdmin={isAdmin} onPlay={onPlay} onEdit={onEditItem} onDelete={onDeleteItem} onProgress={onProgress} onQuiz={onQuiz} onEditQuiz={onEditQuiz} onReprocess={onReprocess} />)}
        </div>
      ) : <p className="text-sm text-text-3 italic">Aucun élément pour l'instant.</p>}
    </section>
  );
}

export default function FormationPage() {
  const { api, token, isAdmin } = useAuth();
  const [familles, setFamilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [famDlg, setFamDlg] = useState(null);
  const [itemDlg, setItemDlg] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [suivi, setSuivi] = useState(false);
  const [quizItem, setQuizItem] = useState(null);
  const [quizEdit, setQuizEdit] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api().get('/formation/familles'); setFamilles(data.familles || []); }
    catch { toast.error('Chargement impossible'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const reprocess = async (it, action = 'reprocess') => {
    try {
      await api().post(`/formation/items/${it.id}/${action}`);
      toast.success(action === 'skip-conversion' ? "Vidéo d'origine conservée" : 'Préparation relancée');
      load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Relance impossible'); }
  };
  const processing = familles.some(f => f.items.some(i => (i.processing && !i.processing.error) || (i.video_conv && !i.video_conv.error)));
  useEffect(() => { // rafraîchit tant que des slides sont en préparation
    if (!processing) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [processing, load]);

  const patchItem = useCallback((id, patch) => setFamilles(fs => fs.map(f => ({ ...f, items: f.items.map(i => (i.id === id ? { ...i, ...patch } : i)) }))), []);
  const delItem = async (it) => {
    if (!window.confirm(`Supprimer « ${it.titre} » ?`)) return;
    try { await api().delete(`/formation/items/${it.id}`); toast.success('Élément supprimé'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Suppression impossible'); }
  };
  const delFam = async (f) => {
    if (!window.confirm(`Supprimer la famille « ${f.nom} » ?`)) return;
    try { await api().delete(`/formation/familles/${f.id}`); toast.success('Famille supprimée'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Suppression impossible'); }
  };
  const all = familles.flatMap(f => f.items);
  const count = (t) => all.filter(i => i.type === t).length;

  return (
    <AppLayout>
      <div className="max-w-[1600px] mx-auto space-y-10" data-testid="formation-page">
        <PageHeader overline="Formation · Diffusion" title="Formation commerciale">
          {isAdmin && (
            <>
              <button onClick={() => setSuivi(true)} className="h-10 px-4 rounded-ctrl border border-border bg-surface text-sm font-semibold text-ink inline-flex items-center gap-2 hover:bg-surface-2 transition-colors" data-testid="formation-suivi-btn"><BarChart3 className="w-4 h-4" /> Suivi</button>
              <button onClick={() => setFamDlg({})} className="h-10 px-4 rounded-ctrl border border-border bg-surface text-sm font-semibold text-ink inline-flex items-center gap-2 hover:bg-surface-2 transition-colors" data-testid="formation-new-famille"><FolderPlus className="w-4 h-4" /> Nouvelle famille</button>
              <button onClick={() => setItemDlg({})} disabled={!familles.length} className="h-10 px-4 rounded-ctrl bg-orange text-orange-on text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="formation-new-item"><Plus className="w-4 h-4" /> Ajouter un élément</button>
            </>
          )}
        </PageHeader>

        <SceneBand className="flex flex-wrap items-center gap-y-4">
          <div className="pr-6 mr-2 flex items-center gap-3">
            <GraduationCap className="w-9 h-9 text-orange" strokeWidth={1.6} />
            <p className="text-ondark-2 text-sm max-w-xs">Books à diffuser en diaporama, audios et tutos. Masquez les slides inutiles pour votre rendez-vous.</p>
          </div>
          <SceneStat label="Familles" value={familles.length} first />
          <SceneStat label="Diaporamas" value={count('book')} />
          <SceneStat label="Audios" value={count('audio')} />
          <SceneStat label="Vidéos" value={count('video')} />
        </SceneBand>

        {loading ? <p className="text-text-3">Chargement…</p> : !familles.length ? (
          <div className="rounded-card bg-surface p-10 text-center text-text-3" data-testid="formation-empty">
            {isAdmin ? 'Commencez par créer une famille (ex. Présentation commerciale, Tuto formation Capex…).' : 'Aucun contenu de formation pour le moment.'}
          </div>
        ) : <div className="space-y-12 pt-4">{familles.map(f => (
          <FamilleSection key={f.id} fam={f} token={token} isAdmin={isAdmin} onPlay={setPlaying}
            onEditItem={setItemDlg} onDeleteItem={delItem} onAddItem={(fid) => setItemDlg({ famille_id: fid, _new: true })}
            onEditFam={setFamDlg} onDeleteFam={delFam} onProgress={patchItem} onQuiz={setQuizItem} onEditQuiz={setQuizEdit} onReprocess={reprocess} />
        ))}</div>}
      </div>

      <FamilleDialog open={!!famDlg} famille={famDlg} onClose={() => setFamDlg(null)} onSaved={() => { setFamDlg(null); load(); }} />
      <ItemDialog open={!!itemDlg} item={itemDlg?.id ? itemDlg : null} defaultFamilleId={itemDlg?.famille_id} familles={familles}
        onClose={() => setItemDlg(null)} onSaved={() => { setItemDlg(null); load(); }} />
      {playing?.type === 'book' && <SlideShow item={playing} onClose={() => setPlaying(null)} onPrefsChange={patchItem} />}
      {playing?.type === 'video' && <VideoModal item={playing} token={token} onClose={() => setPlaying(null)} onProgress={patchItem} />}
      {isAdmin && <SuiviDialog open={suivi} onClose={() => setSuivi(false)} />}
      <QuizPlayer item={quizItem} onClose={() => setQuizItem(null)}
        onDone={(id, r) => patchItem(id, { my_quiz: { best_score: Math.max(r.score, quizItem?.my_quiz?.best_score || 0), passed: r.passed || !!quizItem?.my_quiz?.passed, attempts: (quizItem?.my_quiz?.attempts || 0) + 1 } })} />
      {isAdmin && <QuizEditor item={quizEdit} onClose={() => setQuizEdit(null)}
        onSaved={(n, pass) => { patchItem(quizEdit.id, { quiz_count: n, quiz_pass_score: pass }); setQuizEdit(null); }} />}
    </AppLayout>
  );
}
